import api from './api';

export type PayoutBatchStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'SCHEDULED'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'PARTIALLY_COMPLETED'
  | 'FAILED'
  | 'REJECTED'
  | 'CANCELLED'

export type PayoutItemStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED'

export interface PayoutItem {
  id: string
  batchId: string
  recipientType: string
  recipientId: string
  recipientName: string
  recipientEmail: string | null
  bankName: string
  bankCode: string | null
  accountNumber: string
  accountNumberLast: string
  accountHolderName: string
  amount: string
  description: string | null
  reference: string | null
  status: PayoutItemStatus
  statusReason: string | null
  processedAt: string | null
  transferId: string | null
  createdAt: string
  updatedAt: string
}

export interface PayoutBatch {
  id: string
  batchNumber: string
  name: string
  type: string
  totalAmount: string
  currency: string
  itemCount: number
  status: PayoutBatchStatus
  scheduledFor?: string | null
  approvedAt?: string | null
  rejectionReason?: string | null
  processedAt?: string | null
  metadata?: Record<string, unknown> | null
  createdAt: string
  updatedAt: string
  items?: PayoutItem[]
  statistics?: {
    totalAmount: number
    completedAmount: number
    pendingAmount: number
    failedAmount: number
    completionRate: number
  }
}

export interface PayoutBatchesResponse {
  data: PayoutBatch[]
  pagination: { page: number; limit: number; total: number; totalPages: number }
  summary: {
    totalAmount: number
    pendingBatches: number
    processingBatches: number
    completedBatches: number
  }
}

export const payoutService = {
  async list(status?: string, page = 1): Promise<PayoutBatchesResponse> {
    const response = await api.get<PayoutBatchesResponse>('/payout/batches', {
      params: { status: status || undefined, page },
    })
    return response.data
  },

  async getDetail(id: string): Promise<PayoutBatch> {
    const response = await api.get<PayoutBatch>(`/payout/batches/${id}`)
    return response.data
  },

  /** Approve a PENDING batch; notes are optional and stored in metadata. */
  async approve(id: string, notes?: string): Promise<PayoutBatch> {
    const response = await api.patch<PayoutBatch>(`/payout/batches/${id}/approve`, { notes })
    return response.data
  },

  async reject(id: string, reason: string): Promise<PayoutBatch> {
    const response = await api.patch<PayoutBatch>(`/payout/batches/${id}/reject`, { reason })
    return response.data
  },

  /**
   * Process an APPROVED/SCHEDULED batch: writes the double-entry ledger and
   * marks items COMPLETED. The bank transfer itself happens offline.
   */
  async process(id: string): Promise<PayoutBatch> {
    const response = await api.post<PayoutBatch>(`/payout/batches/${id}/process`)
    return response.data
  },

  async cancel(id: string): Promise<PayoutBatch> {
    const response = await api.patch<PayoutBatch>(`/payout/batches/${id}/cancel`)
    return response.data
  },

  /** Retry a FAILED item (max 3 retries, enforced server-side). */
  async retryItem(itemId: string): Promise<PayoutItem> {
    const response = await api.patch<PayoutItem>(`/payout/items/${itemId}/retry`)
    return response.data
  },

  /**
   * Mark one item as transferred (offline bank payment done). Completing the
   * last item also completes the batch, which flips the inheritance claim
   * to PAID on the next claims sync.
   */
  async markTransferred(itemId: string, transferReference?: string): Promise<PayoutItem> {
    const response = await api.patch<PayoutItem>(`/payout/items/${itemId}/transfer`, {
      transferReference: transferReference || undefined,
    })
    return response.data
  },

  /**
   * Fill in or correct a payout item's recipient bank details (collected
   * offline) before the transfer is marked.
   */
  async updateItemBank(
    itemId: string,
    data: {
      bankName: string
      accountNumber: string
      accountHolderName: string
      bankCode?: string
    },
  ): Promise<PayoutItem> {
    const response = await api.patch<PayoutItem>(`/payout/items/${itemId}/bank`, data)
    return response.data
  },
}
